import { FC, useEffect, useMemo, useState } from "react";
import useIsComponentMounted from "../../../../../common/hooks/use-is-component-mounted";
import useRouteQueryParams from "../../../../../common/hooks/use-route-query-params";
import usePagination from "../../../../../common/hooks/use-pagination";
import HeadingComponent from "../../../../../common/components/heading/heading.component";
import ListingSortSelectComponent from "../../../../../common/components/listing/filter/sort/select/listing-sort-select.component";
import CardComponent from "../../../../../common/components/card/card.component";
import PaginationComponent from "../../../../../common/components/pagination/pagination.component";
import DateRange from "../../../../../common/types/date-range";
import { useAppContext } from "../../../../../context/app.context";
import fileDownloadService from "../../../../../common/utils/file-download/file-download.service";
import notificationService from "../../../../../common/utils/notification/notification.service";
import ButtonComponent from "../../../../../common/components/button/button.component";
import DateRangeInputComponent from "../../../../../common/components/form/input/date-range/date-range-input.component";
import BillingsTaxiRouteListingViewBasicProps from "../common/types/billings-taxi-route-listing-view-basic-props";
import BillingsTaxiRouteListingByTaxiRouteQueryParams from "./common/types/billings-taxi-route-listing-by-taxi-route-query-params";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import BillingsTaxiRouteListingFilter, {
  BillingsTaxiRouteListingRouteStartDateFilter,
} from "../common/types/billings-taxi-route-listing-filter";
import BillingsTaxiRouteListingItem from "../common/types/billings-taxi-route-listing-item";
import BillingsTaxiRouteListingStatsSummary from "../common/types/billings-taxi-route-listing-stats-summary";
import BillingsTaxiRouteListingSortKey from "../common/types/billings-taxi-route-listing-sort-key";
import BillingsTaxiRouteListingSortSelectOption from "../common/types/billings-taxi-route-listing-sort-select-option";
import billingsTaxiRouteListingSortHelper from "../common/billings-taxi-route-listing-sort.helper";
import billingsTaxiRouteListingRequestFactory from "../common/billings-taxi-route-listing-request.factory";
import BillingsTaxiRouteListingFilterType from "../common/types/billings-taxi-route-listing-filter-type";
import billingsTaxiRouteListingReportRequestFactory from "../common/billings-taxi-route-listing-report-request.factory";
import BillingsTaxiRouteListingFiltersSelectComponent from "../common/filters/select/billings-taxi-route-listing-filters-select.component";
import BillingsTaxiRouteListingFiltersBadgeListComponent from "../common/filters/list/billings-taxi-route-listing-filters-badge-list.component";
import BillingsTaxiRouteListingStatsSummaryComponent from "../common/stats-summary/billings-taxi-route-listing-stats-summary.component";
import BillingsTaxiRouteListingTableComponent from "../common/table/billings-taxi-route-listing-table.component";
import useAbort from "../../../../../common/hooks/use-abort";
import useTaxiCorporationPlanEntryBillings from "../../../../../common/services/taxi-corporation/plan-entry-billings/list/use-taxi-corporation-plan-entry-billings";
import billingsTaxiRouteListingFactory from "../common/billings-taxi-route-listing.factory";
import taxiCorporationService from "../../../../../common/services/taxi-corporation/taxi-corporation.service";
import TaxiCorporationPlanEntryBillingsDownload from "../../../../../common/services/taxi-corporation/plan-entry-billings/download/taxi-corporation-plan-entry-billings-download";
import billingsTaxiRouteListingRouteQueryParamsService from "../common/billings-taxi-route-listing-route-query-params.service";

type BillingsTaxiRouteListingByTaxiProps =
  BillingsTaxiRouteListingViewBasicProps;

const BillingsTaxiRouteListingByTaxiComponent: FC<
  BillingsTaxiRouteListingByTaxiProps
> = (props) => {
  const { user, selectedAppLanguage } = useAppContext();

  const taxiCorporationUuid = user?.aspects.taxiOfficer?.taxiCorporationUuid!;

  const isComponentMounted = useIsComponentMounted();

  const [routeQueryParams, setRouteQueryParams] =
    useRouteQueryParams<BillingsTaxiRouteListingByTaxiRouteQueryParams>();

  const translations =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations();

  const [filters, setFilters] = useState<BillingsTaxiRouteListingFilter[]>(() =>
    billingsTaxiRouteListingRouteQueryParamsService.getFilters(
      routeQueryParams,
    ),
  );

  const taxiCorporationPlanEntryBillings =
    useTaxiCorporationPlanEntryBillings();
  const taxiCorporationPlanEntryBillingsAbort = useAbort();

  const downloadAbort = useAbort();

  const [listingItems, setListingItems] = useState<
    BillingsTaxiRouteListingItem[]
  >([]);
  const [statsSummary, setStatsSummary] =
    useState<BillingsTaxiRouteListingStatsSummary | null>(null);
  const [totalResults, setTotalResults] = useState(0);

  const [isReportFetching, setIsReportFetching] = useState(false);

  const [selectedSortKey, setSelectedSortKey] =
    useState<BillingsTaxiRouteListingSortKey | null>(
      () =>
        billingsTaxiRouteListingRouteQueryParamsService.getSortKey(
          routeQueryParams,
        ) ?? null,
    );

  const sortSelectOptions: BillingsTaxiRouteListingSortSelectOption[] = useMemo(
    () => billingsTaxiRouteListingSortHelper.getSelectOptions(),
    [selectedAppLanguage],
  );

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: totalResults,
    defaultPageSize:
      billingsTaxiRouteListingRouteQueryParamsService.getPageSize(
        routeQueryParams,
      ),
    defaultPage:
      billingsTaxiRouteListingRouteQueryParamsService.getPage(routeQueryParams),
  });

  useEffect(() => {
    if (!taxiCorporationPlanEntryBillings.data) return;

    const listingItems = billingsTaxiRouteListingFactory.createListingItems(
      taxiCorporationPlanEntryBillings.data.data,
    );

    const statsSummary = billingsTaxiRouteListingFactory.createStatsSummary(
      taxiCorporationPlanEntryBillings.data.stats,
    );

    setListingItems(listingItems);
    setStatsSummary(statsSummary);
    setTotalResults(taxiCorporationPlanEntryBillings.data.totalCount);
  }, [taxiCorporationPlanEntryBillings.data]);

  const fetchListing = () => {
    const params = billingsTaxiRouteListingRequestFactory.createRequest(
      taxiCorporationUuid,
      page,
      pageSize,
      filters,
      selectedSortKey,
    );

    taxiCorporationPlanEntryBillings.load(
      params,
      taxiCorporationPlanEntryBillingsAbort.signal,
    );
  };

  useEffect(() => {
    if (!page || !pageSize) {
      return;
    }

    fetchListing();

    return taxiCorporationPlanEntryBillingsAbort.revoke;
  }, [filters, selectedSortKey, page, pageSize]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const queryParams =
      billingsTaxiRouteListingRouteQueryParamsService.createRouteQueryParams(
        filters,
        selectedSortKey,
        page,
        pageSize,
      );

    setRouteQueryParams(queryParams);
  }, [filters, selectedSortKey, page, pageSize]);

  const addNewFilter = (newFilter: BillingsTaxiRouteListingFilter) => {
    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const deleteFilter = (index: number) => {
    const newFilters = filters.filter((filter, _index) => _index !== index);

    setFilters(newFilters);
    setPage(1);
  };

  const deleteAllFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const onPageChange = (page: number) => {
    setPage(page);
    window.scroll({ top: 0, behavior: "smooth" });
  };

  const onPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  const selectedSortSelectOption = useMemo(() => {
    return (
      sortSelectOptions.find((item) => item.value === selectedSortKey) ?? null
    );
  }, [selectedSortKey, selectedAppLanguage]);

  const startDateFilterValue = filters.find(
    (filter) =>
      filter.type === BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
  )?.value as BillingsTaxiRouteListingRouteStartDateFilter["value"] | undefined;

  const onOrderStartDateFilterValueChange = (dateRange: DateRange | null) => {
    if (!dateRange) {
      const newFilters = filters.filter(
        (filter) =>
          filter.type !== BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
      );

      setFilters(newFilters);
      return;
    }

    const isFilterExists = !!filters.find(
      (filter) =>
        filter.type === BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
    );

    const newFilter: BillingsTaxiRouteListingRouteStartDateFilter = {
      type: BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
      value: {
        from: dateRange.from!,
        to: dateRange.to!,
      },
    };

    if (isFilterExists) {
      const newFilters = [
        ...filters.filter(
          (filter) =>
            filter.type !== BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
        ),
        newFilter,
      ];

      setFilters(newFilters);
      setPage(1);
      return;
    }

    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const onReportFetchSuccess = async (
    data: TaxiCorporationPlanEntryBillingsDownload,
  ) => {
    fileDownloadService.downloadFromBlob(data.data, data.filename);
  };

  const onReportFetchFailure = () => {
    notificationService.error(
      translations.report.failureDownloadingNotificationText,
    );
  };

  const fetchReport = () => {
    setIsReportFetching(true);

    const params = billingsTaxiRouteListingReportRequestFactory.createRequest(
      taxiCorporationUuid,
      filters,
      selectedSortKey,
    );

    taxiCorporationService
      .getPlanEntryBillingsReport(params, downloadAbort.signal)
      .then(onReportFetchSuccess)
      .catch(onReportFetchFailure)
      .finally(() => setIsReportFetching(false));
  };

  const onReportDownloadButtonClick = () => {
    fetchReport();
  };

  const ReportDownloadButton = (
    <ButtonComponent
      onClick={onReportDownloadButtonClick}
      type="primary"
      isLoading={isReportFetching}
      title={translations.report.downloadButtonTitle}
      idForTesting="billings-taxi-route-listing-by-taxi-report-download-button"
    >
      {translations.report.downloadButtonText}
    </ButtonComponent>
  );

  const isReportDownloadButtonVisible = !!taxiCorporationUuid;

  return (
    <div className="billings_taxi_route_listing">
      <HeadingComponent
        title={translations.header.headingText}
        actions={[
          ...props.actionButtons,
          isReportDownloadButtonVisible && ReportDownloadButton,
        ]}
      />
      <div className="billings_taxi_route_listing_tools">
        <div className="d-flex">
          <BillingsTaxiRouteListingFiltersSelectComponent
            filters={filters}
            onAddNewFilter={addNewFilter}
          />
          <DateRangeInputComponent
            date={startDateFilterValue ?? null}
            onChange={onOrderStartDateFilterValueChange}
            classNames={{ root: "ml-2" }}
            placeholder={
              translations.filters.searchByStartDateSelectInputPlaceholder
            }
            idForTesting="billings-taxi-route-listing-by-taxi-date-range"
          />
        </div>
        <ListingSortSelectComponent
          onChange={(option) => setSelectedSortKey(option?.value!)}
          options={sortSelectOptions}
          value={selectedSortSelectOption}
          idForTesting="billings-taxi-route-listing-by-taxi-sort"
        />
      </div>
      <BillingsTaxiRouteListingFiltersBadgeListComponent
        filters={filters}
        onDeleteFilterClick={deleteFilter}
        onDeleteAllFiltersButtonClick={deleteAllFilters}
      />
      {!!listingItems.length && (
        <BillingsTaxiRouteListingStatsSummaryComponent
          isError={taxiCorporationPlanEntryBillings.isError}
          isLoading={taxiCorporationPlanEntryBillings.isLoading}
          statsSummary={statsSummary}
        />
      )}
      <CardComponent classNames={{ root: "mt-4" }}>
        <BillingsTaxiRouteListingTableComponent
          listingItems={listingItems}
          isError={taxiCorporationPlanEntryBillings.isError}
          isLoading={taxiCorporationPlanEntryBillings.isLoading}
        />
        <div className="billings_taxi_route_listing__pagination_wrapper">
          <PaginationComponent
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            page={page}
            pageSize={pageSize}
            totalResults={totalResults}
          />
        </div>
      </CardComponent>
    </div>
  );
};

export default BillingsTaxiRouteListingByTaxiComponent;
